import { useQuery } from '@tanstack/react-query';
import { AxiosProxy } from '../../api/axiosProxy/axiosProxy';
import { rejectFailedRequestOrEmptyData } from '../../lib/request/rejectFailedRequest/rejectFailedRequest';
import { Organization } from '../../types/organization.interface';
import { OrganizationSubscription } from '@manifest-cyber/types/interface/db';

interface Params {
  organizationId?: string | null;
}

interface Options {
  staleTime?: number;
  cacheTime?: number;
}

const DEFAULT_SUBSCRIPTION: OrganizationSubscription = { aibom: false, sbom: true };

function makeFetchUrl({ organizationId }: Params) {
  return `organization${organizationId ? `/${organizationId}` : ''}`;
}

export function makeQueryKey({ organizationId }: Params) {
  const fetchUrl = makeFetchUrl({ organizationId });
  return ['currentOrganizationSubscriptions', { _id: organizationId }, `/${fetchUrl}`];
}

export const useFetchOrganizationSubscriptions = (
  { organizationId }: Params,
  options?: Options,
) => {
  const fetchUrl = makeFetchUrl({ organizationId }); const query = useQuery({
    enabled: Boolean(organizationId),
    queryKey: makeQueryKey({ organizationId }),
    queryFn: async () => {
      const response = await AxiosProxy.get({ url: fetchUrl });
      const validatedResponse =
        await rejectFailedRequestOrEmptyData<[Organization]>(response);
      const subscriptions = validatedResponse.data[0].subscriptions;

      // Handle legacy customers: treat undefined, null, or empty objects as needing default
      if (!subscriptions || Object.keys(subscriptions).length === 0) {
        return null;
      }

      return subscriptions;
    },
    staleTime: options?.staleTime || 60 * 1000, // 1 minute
    cacheTime: options?.cacheTime || 60 * 1000, // 1 minute
  });

  const computedSubscriptions: OrganizationSubscription =
    query.data || DEFAULT_SUBSCRIPTION;

  const hasSubscriptions = (
    requiredSubscriptions: Array<keyof OrganizationSubscription>,
  ) => {
    if (query.isLoading || query.isError) {
      return false;
    }
    return requiredSubscriptions.every(
      (subscription) => computedSubscriptions[subscription],
    );
  };

  const onlyAibomSubscription =
    computedSubscriptions.aibom && !computedSubscriptions.sbom;
  const onlySbomSubscription = !computedSubscriptions.aibom && computedSubscriptions.sbom;
  const aibomAndSbomSubscription =
    computedSubscriptions.aibom && computedSubscriptions.sbom;

  return {
    isLoadingSubscription: query.isLoading,
    isErrorSubscription: query.isError,
    subscriptions: computedSubscriptions,
    hasSubscriptions,
    onlyAibomSubscription,
    onlySbomSubscription,
    aibomAndSbomSubscription,
  };
};
